import { Component, ElementRef, OnInit, ViewChild, Input, Output, EventEmitter } from '@angular/core';
import { environment } from '@env/environment';
import * as Mapboxgl from 'mapbox-gl';

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss']
})
export class MapComponent implements OnInit {
  @ViewChild('coordinates', { static: true }) coordinatesElement: ElementRef;
  @ViewChild('map', { static: true }) mapElement: ElementRef;

  private map: Mapboxgl.Map;
  private marker: Mapboxgl.Marker;
  private zoom = 15;
  private defaultCenter: [number, number] = [-58.46651124133983, -34.63104776379445]; // Buenos Aires

  @Input() latitud: number;
  @Input() longitud: number;
  @Output() coordinatesChanged = new EventEmitter<{ latitud: number; longitud: number }>();

  constructor() {
    // Asignación forzada a cualquier para evitar el error de solo lectura
    (Mapboxgl as any).accessToken = environment.mapBoxToken;
  }

  ngOnInit(): void {
    this.initializeMap();
  }

  private initializeMap(): void {
    this.map = new Mapboxgl.Map({
      container: this.mapElement.nativeElement,
      style: 'mapbox://styles/mapbox/streets-v12',
      zoom: this.zoom,
      center: this.defaultCenter
    });

    this.map.addControl(new Mapboxgl.NavigationControl(), 'top-right');

    this.map.on('load', () => {
      this.updateMarkerFromInputs();
    });
  }

  public setLocation(lat: number, lng: number): void {
    if (isNaN(lat) || isNaN(lng)) return;
    this.latitud = lat;
    this.longitud = lng;
    this.updateMapCenterAndMarker();
  }

  private updateMapCenterAndMarker(): void {
    if (!this.map) return;
    const lngLat: [number, number] = [this.longitud, this.latitud];
    this.map.jumpTo({ center: lngLat, zoom: this.zoom });
    this.updateMarker(lngLat);
  }

  private updateMarker(lngLat: [number, number]): void {
    if (this.marker) {
      this.marker.setLngLat(lngLat);
    } else {
      this.marker = new Mapboxgl.Marker({ draggable: true })
        .setLngLat(lngLat)
        .addTo(this.map);

      this.marker.on('dragend', () => {
        const lngLat = this.marker.getLngLat();
        this.latitud = lngLat.lat;
        this.longitud = lngLat.lng;
        this.showCoordinates();
        this.coordinatesChanged.emit({ latitud: this.latitud, longitud: this.longitud });
      });
    }
    this.showCoordinates();
  }

  private showCoordinates(): void {
    if (this.coordinatesElement) {
      this.coordinatesElement.nativeElement.style.display = 'block';
      this.coordinatesElement.nativeElement.innerHTML = `Lat: ${this.latitud.toFixed(6)}<br/>Lng: ${this.longitud.toFixed(6)}`;
    }
  }

  private geocodeAddress(address: string): Promise<{ lat: number; lng: number }> {
    const token = (Mapboxgl as any).accessToken;
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${token}&limit=1`;
    return fetch(url)
      .then(response => response.json())
      .then(data => {
        if (data.features && data.features.length > 0) {
          const [lng, lat] = data.features[0].center;
          return { lat, lng };
        } else {
          throw new Error('Dirección no encontrada');
        }
      });
  }

  public searchFromAddress(address: string): Promise<void> {
    return this.geocodeAddress(address)
      .then(result => {
        this.latitud = result.lat;
        this.longitud = result.lng;
        this.updateMapCenterAndMarker();
        this.coordinatesChanged.emit({ latitud: this.latitud, longitud: this.longitud });
      });
  }

  private updateMarkerFromInputs(): void {
    if (!isNaN(this.latitud) && !isNaN(this.longitud)) {
      this.updateMapCenterAndMarker();
    } else {
      this.latitud = this.defaultCenter[1];
      this.longitud = this.defaultCenter[0];
      this.updateMapCenterAndMarker();
    }
  }
}